import React, { useState } from 'react';

import {
  cn,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from '@crossnokaye/ux-common';
import { InputInlineValueChangeLabel } from '@crossnokaye/ux-common/ckds';

import { Stack } from '@/ux-common/components/stack/stack';

interface SequenceBoxProps {
  // Children to display inside the box. Typically the stage value.
  children?: React.ReactNode;
  // Classname to apply to the box. Typically helps with background color.
  className?: string;
  // Tooltip text to display when disabled (e.g., permission message).
  disabledTooltipText?: string;
  // Whether interactions are disabled (e.g., due to permissions or read-only mode).
  isDisabled?: boolean;
  // Whether this cell has been modified from its original value.
  isDirty?: boolean;
  // Whether this cell is unavailable (disables click and shows not-allowed cursor).
  isUnavailable?: boolean;
  // Function to call when the box is clicked.
  onClick?: () => void;
  // The original value of the box. Typically the original stage value.
  originalValue?: string;
  // Style to apply to the box. Typically helps with background color.
  style?: React.CSSProperties;
  // Tooltip content to display when the box is hovered.
  tooltipContent: string;
}

export const SequenceBox: React.FC<SequenceBoxProps> = ({
  children,
  className,
  disabledTooltipText,
  isDisabled,
  isDirty,
  isUnavailable,
  onClick,
  originalValue,
  style,
  tooltipContent,
}) => {
  // Manually manage the open state of the tooltip, because radix-ui closes tooltip on
  // activation (mouse click) and the sequencer UI requires us to leave it open.
  const [isOpen, setIsOpen] = useState(false);

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip open={isOpen}>
        <TooltipTrigger
          asChild
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
        >
          <div
            className={cn(
              'flex items-center justify-center h-full relative isolate',
              !isUnavailable &&
                'hover:outline hover:outline-2 hover:outline-ckds-input-border-focus',
              onClick && !isDisabled ? 'hover:cursor-pointer' : '',
              isUnavailable && 'hover:cursor-not-allowed',
              isDirty && 'outline outline-2 outline-warning'
            )}
            onClick={isDisabled ? undefined : onClick}
          >
            {/* Background layer with opacity */}
            <div className={cn('absolute inset-0', className)} style={style} />
            {/* Top-right corner triangle indicator for dirty state */}
            {isDirty && (
              <div
                className="absolute top-0 right-0 z-[2] w-0 h-0"
                style={{
                  borderTop: '16px solid hsl(var(--ckds-warning))',
                  borderLeft: '16px solid transparent',
                }}
              />
            )}
            {/* Content layer without opacity */}
            <div className="relative z-[1] flex items-center justify-center">{children}</div>
          </div>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent align={'center'} side="bottom" className={cn('m-0 gap-0.5')}>
            <Stack className="stack-thin">
              <div className="flex items-center gap-1">
                <span className="font-bold">{tooltipContent}</span>
                {isDirty && originalValue && (
                  <InputInlineValueChangeLabel
                    className={'text-muted-foreground'}
                    units={''}
                    oldValue={originalValue?.toString() ?? ''}
                  />
                )}
              </div>
              {isDisabled && disabledTooltipText && <span>{disabledTooltipText}</span>}
            </Stack>
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
};
